import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaFileDownload, FaFileUpload, FaSpinner } from 'react-icons/fa';

/**
 * Reusable Bulk Import Component
 * @param {string} tableName - Name of the module (e.g., 'Vendors') - used for file naming.
 * @param {Array} columns - Array of objects defining structure: [{ header: 'Name', key: 'name', sample: 'John Doe' }]
 * @param {string} apiEndpoint - The full backend URL to upload the file to.
 * @param {function} onRefresh - Function to refresh the table after a successful upload.
 */
const BulkImportControl = ({ tableName, columns, apiEndpoint, onRefresh }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    // 🟢 1. GENERATE & DOWNLOAD SAMPLE EXCEL
    const handleDownloadSample = () => {
        // Create a sample row based on the 'sample' property in columns
        const sampleRow = {};
        columns.forEach(col => {
            sampleRow[col.header] = col.sample || 'Sample Data';
        });

        // Create Workbook
        const worksheet = XLSX.utils.json_to_sheet([sampleRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

        // Write & Save
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        
        saveAs(data, `${tableName}_import_template.xlsx`);
        toast.info(`Downloaded template for ${tableName}. Please fill and upload.`);
    };

    // 🟢 2. HANDLE FILE UPLOAD
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('token');
            
            // Upload to Backend
            await axios.post(apiEndpoint, formData, {
                headers: { 
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}` 
                }
            });

            toast.success("Bulk Import Successful!");
            if (onRefresh) onRefresh(); // Reload the data table
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || "Import failed. Check your file format.");
        } finally {
            setUploading(false);
            e.target.value = null; // Reset input so you can re-upload the same file if needed
        }
    };

    return (
        <div className="flex gap-2 items-center">
            {/* Download Template Button */}
            <button 
                onClick={handleDownloadSample}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition shadow-sm"
                title="Download Excel Template"
            >
                <FaFileDownload className="text-green-600"/> 
                <span>Template</span>
            </button>

            {/* Upload Button */}
            <div className="relative">
                <input 
                    type="file" 
                    accept=".xlsx, .xls, .csv"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                />
                <button 
                    onClick={() => fileInputRef.current.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-sm"
                >
                    {uploading ? <FaSpinner className="animate-spin"/> : <FaFileUpload />}
                    <span>{uploading ? 'Uploading...' : 'Import Data'}</span>
                </button>
            </div>
        </div>
    );
};

export default BulkImportControl;